import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { groupManagementService } from '../../services/groupManagementService';

// AUTHCFG-002 (Round 6 sweep #116, finding 26419fe4): the previous
// hardcoded fallback GUID `d63b7a80-...` would silently bind admin
// gating to whatever group held that ID in the deployed tenant. Match
// the fail-closed posture used in `frontend/src/config/features.ts`
// (R5 T3 #87) — when the env var is unset, no group is granted.
const PROFILE_ADMIN_GROUP_ID = process.env.REACT_APP_PROFILE_ADMIN_GROUP_ID || '';

// Owner user IDs who can see and use this switch to test permissions
const OWNER_USER_IDS = (process.env.REACT_APP_OWNER_USER_IDS || '')
  .split(',')
  .map(id => id.trim())
  .filter(id => id.length > 0);

const AdminPermissionSwitch: React.FC = () => {
  const { hasAnyGroup, updateGroups, user } = useAuth();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Debug logging in development
  if (process.env.NODE_ENV === 'development' && user) {
    console.log('[AdminPermissionSwitch] User IDs:', {
      localAccountId: user.localAccountId,
      homeAccountId: user.homeAccountId,
      username: user.username,
      configuredOwnerIds: OWNER_USER_IDS
    });
  }

  // Check membership using the actual group ID
  const isMember = hasAnyGroup([PROFILE_ADMIN_GROUP_ID]);

  // Check if current user is an owner (can see the switch)
  // localAccountId is the user's object ID (oid claim) - this is what we typically want
  const isOwner = OWNER_USER_IDS.length === 0 || OWNER_USER_IDS.some(ownerId => {
    // Handle both formats: just the OID, or the full homeAccountId format (oid.tenantId)
    const normalizedOwnerId = ownerId.trim().toLowerCase();
    const userLocalId = user?.localAccountId?.toLowerCase();
    const userHomeId = user?.homeAccountId?.toLowerCase();

    return userLocalId === normalizedOwnerId ||
           userHomeId === normalizedOwnerId ||
           userHomeId?.startsWith(normalizedOwnerId + '.');
  });

  // Only show to application owners for testing purposes
  if (!isOwner) {
    return null;
  }

  const handleToggle = async () => {
    if (isProcessing) {
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      if (isMember) {
        await groupManagementService.removeCurrentUserFromGroup(PROFILE_ADMIN_GROUP_ID);
        updateGroups(previous => previous.filter(group => group !== PROFILE_ADMIN_GROUP_ID));
      } else {
        await groupManagementService.addCurrentUserToGroup(PROFILE_ADMIN_GROUP_ID);
        updateGroups(previous => {
          if (previous.includes(PROFILE_ADMIN_GROUP_ID)) {
            return previous;
          }
          return [...previous, PROFILE_ADMIN_GROUP_ID];
        });
      }
    } catch (err) {
      console.error('Failed to toggle security group membership', err);
      setError('Failed to update permissions. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="admin-permission-switch" title="Toggle profile admin group membership">
      <label className={`switch ${isProcessing ? 'disabled' : ''}`}>
        <input
          type="checkbox"
          checked={isMember}
          onChange={handleToggle}
          disabled={isProcessing}
        />
        <span className="slider" />
      </label>
      <div className="switch-details">
        <span className="switch-label">Profile Admin Access</span>
        <span className="switch-status">{isMember ? 'granted' : 'revoked'}</span>
        {error && <span className="switch-error">{error}</span>}
      </div>
    </div>
  );
};

export default AdminPermissionSwitch;
